<script>
import axios from 'axios';
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { Grid, Navigation, Virtual, FreeMode } from 'swiper/modules';
import PhotoSwipeLightbox from 'photoswipe/lightbox';

import 'photoswipe/style.css';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { assetURL } from '../utils/url';

export default {
    components: {
        Swiper,
        SwiperSlide,
    },
    setup() {
        let swiperRef = null;
        const setSwiperRef = (swiper) => {
            swiperRef = swiper;
        };
        const slideTo = (index) => {
            swiperRef.slideTo(index - 1, 0);
        };
        return {
            swiperRef: null,
            setSwiperRef,
            slideTo,
            modules: [Grid, Navigation, Virtual, FreeMode],
            modulesDesktop: [Navigation, Virtual],
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
        };
    },
    data() {
        return {
            images: [],
            items: [],
            slug: this.slug,
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight,
            staticURL: `${assetURL}images/properties/gallery/1.jpg`,
            galleryID: 'propertiesGallery',
            lightbox: null,
            breakpoints: {
                '430': {
                    modules: this.modules,
                    slidesPerView: 2,
                    grid: {rows: 2,},
                },
                '640': {
                    modules: this.modules,
                    slidesPerView: 2,
                    grid: {rows: 2,},
                },
                '768': {
                    modules: this.modules,
                    slidesPerView: 2,
                    grid: {rows: 2,},
                },
                '1024': {
                    modules: this.modules,
                    slidesPerView: 2,
                    grid: {rows: 2,},
                },
                '1200': {
                    modules: this.modulesDesktop,
                    slidesPerView: '3.50',
                    grid: {rows: 1,},
                },
            },
        };
    },
    computed: {
        slug() {
            const slug = new URL(location.href);
            return slug;
        },
    },
    mounted() {
        const pathname = location.pathname.split('/');
        this.getPropertyID(pathname[2]);

        // for swiper
        window.addEventListener('resize', this.getDimensions);

        // for lightbox images
        if (!this.lightbox) {
            this.lightbox = new PhotoSwipeLightbox({
                gallery: `#${this.galleryID}`,
                children: 'a',

                showHideAnimationType: 'none',

                pswpModule: () => import('photoswipe'),
            });

            this.lightbox.init();
        }
    },
    unmounted() {
        // for swiper
        window.removeEventListener('resize', this.getDimensions);

        // for lightbox images
        if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
        }
    },
    methods: {
        getPropertyID(propertySlug) {
            axios
                .get(`/api/property/${propertySlug}`)
                .then((response) => {
                    this.$data.items = response.data.property;
                    this.$data.images = response.data.property.gallery;
                })
                .catch((error) => {
                    this.errored = true;
                });
        },
        sanitize(url) {
            return sanitizeUrl(url);
        },
        getDimensions() {
            this.width = document.documentElement.clientWidth;
            this.height = document.documentElement.clientHeight;
        },
        getImageSize() {
            const img = new Image();
            img.src = this.$refs.image.src;
            img.onload = () => {
                this.width = img.width;
                this.height = img.height;
            };
        },
    },
};
</script>

<template>
    <div class="container" v-if="images.length > 0">
        <div class="row mb-30px">
            <div class="col-12 col-xl-11 offset-xl-1">
                <div class="gallery-filter__wrapper">
                    <h2 class="fs-48px fw-extrabold tw-text-darkGreen lh-1">Gallery</h2>
                </div>
            </div>
        </div>
    </div>

    <div class="container-fluid">
        <div class="row">
            <div class="col-12 p-0px">
                <div id="desktop-gallery">
                    <div class="image-gallery__wrapper" >
                        <swiper
                            :modules="modulesDesktop"
                            :centeredSlides="false"
                            :navigation="true"
                            :virtual="false"
                            :lazy="true"
                            class="image-gallery__swiper"
                            :id="galleryID"
                            :breakpoints="breakpoints"
                        >
                            <swiper-slide v-for="(img, index) in images">
                                <a :key="index" :href="this.sanitize(img.path)" :data-pswp-srcset="img.path" target="_blank" rel="noreferrer">
                                    <picture>
                                        <img :src="img.path" loading="lazy"/>
                                    </picture>
                                </a>
                            </swiper-slide>
                        </swiper>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.pswp {
    img {
        object-fit: contain;
        padding: 50px;
    }
}
.swiper-mobile {
    .swiper-slide {
        text-align: center;
        font-size: 18px;
        background: #fff;
        height: calc((100% - 10px) / 2) !important;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .swiper-slide {
        a {
            position: relative;
            display: block;
            width: 100%;
            height: 100%;
            img {
                width: 100%;
                height: 100%;
                max-width: 100%;
                object-fit: cover;
            }
        }
    }
    .swiper-content {
        height: 100%;
        width: 100%;
    }
}
.swiper-grid-column > .swiper-wrapper {
    height: 70vw;
}
.image-gallery__wrapper {
    .swiper-wrapper {
        height: 120vw;
        max-height: 600px;
        flex-direction: column;
        align-items: center;
        align-content: stretch;
        flex-wrap: wrap;
        justify-content: center;
    }
    .swiper-slide {
        cursor: grab;
        text-align: center;
        width: calc(25% - 45px);
        height: calc((100% - 30px) / 2);
    }
    .swiper-content {
        height: auto;
    }
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        max-width: 100%;
    }
}

.image-gallery__swiper,
.swiper-mobile {
    .swiper-button-next,
    .swiper-button-prev {
        height: calc(100% - 10px);
        margin: 0;
        width: 32px;
        background-color: #4a584682;
        color: #ffffff;
        &::after {
            width: 9px;
            height: 35px;
            font-size: 20px;
            display: flex;
            align-items: center;
            flex-direction: row;
        }
    }
    .swiper-button-next {
        right: 0;
    }
    .swiper-button-prev {
        left: 0;
    }
}
.image-gallery__swiper {
    .swiper-button-next,
    .swiper-button-prev {
        top: 15px;
    }
}
.swiper-mobile {
    .swiper-button-next,
    .swiper-button-prev {
        top: 0px;
    }
}

@media (min-width: 1200px) {
    .image-gallery__wrapper {
        .swiper {
            width: 100%;
            max-width: 1920px;
        }
        .swiper-wrapper,
        .swiper-slide {
            display: -webkit-box;
            display: -ms-flexbox;
            display: -webkit-flex;
            display: flex;
            -webkit-box-pack: center;
            -ms-flex-pack: center;
            -webkit-justify-content: center;
            justify-content: center;
            -webkit-box-align: center;
            -ms-flex-align: center;
            -webkit-align-items: center;
            align-items: center;
            height: 240px;
        }
        .swiper-content {
            width: 100%;
            height: 100%;
        }
    }

    .image-gallery__swiper {
        img {
            width: 100%;
            height: 100%;
            -o-object-fit: cover;
            object-fit: cover;
            max-width: 100%;
        }
        .swiper-button-next,
        .swiper-button-prev {
            height: 100%;
            top: 0;
            background-color: #4a5846;
        }
        .swiper-button-prev {
            left: 0px;
        }
    }
}

@media (min-width: 1800px) {
    .image-gallery__wrapper {
        .swiper-wrapper,
        .swiper-slide {
            height: 350px;
        }
    }
}
</style>
